/**
 * Centralized Error Handler Utility
 * Extracts user-friendly error messages from backend API responses
 * Ensures frontend always displays backend error messages instead of creating its own
 */

export interface BackendErrorResponse {
  status: 'error';
  message: string;
  timestamp?: string;
  path?: string;
  method?: string;
  errorType?: string;
  statusCode?: number;
  details?: any;
  technicalMessage?: string;
  stack?: string[];
}

type ErrorRecord = Record<string, unknown>;

const asRecord = (value: unknown): ErrorRecord =>
  typeof value === 'object' && value !== null ? (value as ErrorRecord) : {};

/**
 * Extract user-friendly error message from backend response
 * Priority:
 * 1. Backend error.response.data.message (user-friendly message)
 * 2. Backend error.response.data (if string)
 * 3. Error.message (network/unexpected errors)
 * 4. Generic fallback
 */
export const extractErrorMessage = (error: unknown): string => {
  const err = asRecord(error);
  const response = asRecord(err.response);
  const data = response.data;
  const dataRecord = asRecord(data);
  const body = asRecord(dataRecord.body);
  const message = typeof err.message === 'string' ? err.message : '';

  // Log full error for debugging (visible in console/Network tab)
  console.error('API Error Details:', {
    name: typeof err.name === 'string' ? err.name : undefined,
    code: typeof err.code === 'string' || typeof err.code === 'number' ? err.code : undefined,
    status: typeof response.status === 'number' ? response.status : undefined,
    statusText: typeof response.statusText === 'string' ? response.statusText : undefined,
    message: String(message || 'Unknown').substring(0, 200)
  });

  // Priority 1: Extract backend error message from response data
  if (data !== undefined && data !== null) {
    // Backend returns structured error with message field
    if (typeof dataRecord.message === 'string') {
      return dataRecord.message; // This is the user-friendly message from backend
    }
    
    // Backend returns error as string
    if (typeof data === 'string') {
      return data;
    }
    
    // Backend returns error in body.message (legacy format)
    if (typeof body.message === 'string') {
      return body.message;
    }
  }

  // Priority 2: Network/Axios errors
  if (message) {
    // Network timeout
    if (message.includes('timeout')) {
      return 'Request timed out. Please check your connection and try again.';
    }
    
    // Network error
    if (message.includes('Network Error')) {
      return 'Network connection error. Please check your internet connection.';
    }
    
    // Return the error message as-is (might be from backend)
    return message;
  }

  // Priority 3: Fallback for unknown errors
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Extract full backend error details (for logging/debugging)
 */
export const extractErrorDetails = (error: unknown): BackendErrorResponse | null => {
  const err = asRecord(error);
  const response = asRecord(err.response);

  if (response.data && typeof response.data === 'object') {
    return response.data as BackendErrorResponse;
  }
  return null;
};

/**
 * Check if error is an authentication error
 */
export const isAuthError = (error: unknown): boolean => {
  const err = asRecord(error);
  const response = asRecord(err.response);
  return response.status === 401;
};

/**
 * Check if error is a permission/authorization error
 */
export const isPermissionError = (error: unknown): boolean => {
  const err = asRecord(error);
  const response = asRecord(err.response);
  return response.status === 403;
};

/**
 * Check if error is a not found error
 */
export const isNotFoundError = (error: unknown): boolean => {
  const err = asRecord(error);
  const response = asRecord(err.response);
  return response.status === 404;
};

/**
 * Check if error is a validation error
 */
export const isValidationError = (error: unknown): boolean => {
  const err = asRecord(error);
  const response = asRecord(err.response);
  return response.status === 400;
};

/**
 * Get error status code
 */
export const getErrorStatusCode = (error: unknown): number | null => {
  const err = asRecord(error);
  const response = asRecord(err.response);
  return typeof response.status === 'number' ? response.status : null;
};
